<template>
  <div class="billing-usage-container">
    <!-- Header -->
    <div class="usage-header">
      <div>
        <h3 class="usage-title">{{ $t('billingUsage.title') }}</h3>
        <p class="usage-subtitle">{{ $t('billingUsage.subtitle') }}</p>
      </div>
      <div class="header-actions">
        <b-button variant="outline-primary" size="sm" @click="loadUsage" :disabled="loading">
          <b-icon icon="arrow-clockwise" class="mr-1"></b-icon>
          {{ $t('invoices.refresh') }}
        </b-button>
      </div>
    </div>

    <!-- Month Selector -->
    <div class="month-selector-card">
      <div class="selector-group">
        <b-form-select v-model="selectedMonth" :options="monthOptions" class="month-select" @change="loadUsage"></b-form-select>
        <b-form-select v-model="selectedYear" :options="yearOptions" class="year-select" @change="loadUsage"></b-form-select>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-3">{{ $t('billingUsage.loading') }}</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="!usageData || usageData.usage.length === 0" class="empty-state">
      <div class="empty-icon">
        <b-icon icon="graph-up" font-scale="3"></b-icon>
      </div>
      <h4>{{ $t('billingUsage.noUsage') }}</h4>
      <p>{{ $t('billingUsage.noUsageDesc') }}</p>
    </div>

    <!-- Usage Data -->
    <div v-else class="usage-content">
      <!-- Summary Cards -->
      <div class="summary-cards">
        <div class="summary-card total-card">
          <div class="card-icon">
            <b-icon icon="currency-dollar" font-scale="1.5"></b-icon>
          </div>
          <div class="card-content">
            <span class="card-label">{{ $t('billingUsage.totalUsageCharges') }}</span>
            <span class="card-value">{{ formatCurrency(totalAmount, currency) }}</span>
          </div>
        </div>
        <div class="summary-card services-card">
          <div class="card-icon">
            <b-icon icon="diagram-3" font-scale="1.5"></b-icon>
          </div>
          <div class="card-content">
            <span class="card-label">{{ $t('billingUsage.servicesUsed') }}</span>
            <span class="card-value">{{ usageData.usage.length }}</span>
          </div>
        </div>
        <div class="summary-card subscriptions-card">
          <div class="card-icon">
            <b-icon icon="card-list" font-scale="1.5"></b-icon>
          </div>
          <div class="card-content">
            <span class="card-label">{{ $t('billingUsage.activeSubscriptions') }}</span>
            <span class="card-value">{{ subscriptionCount }}</span>
          </div>
        </div>
        <div class="summary-card total-charges-card">
          <div class="card-icon">
            <b-icon icon="receipt" font-scale="1.5"></b-icon>
          </div>
          <div class="card-content">
            <span class="card-label">{{ $t('billingUsage.subscriptionCharges') }}</span>
            <span class="card-value">{{ formatCurrency(subscriptionTotal, currency) }}</span>
          </div>
        </div>
        <div class="summary-card credits-card">
          <div class="card-icon">
            <b-icon icon="coin" font-scale="1.5"></b-icon>
          </div>
          <div class="card-content">
            <span class="card-label">{{ $t('billingUsage.creditBalance') }}</span>
            <span class="card-value" v-if="creditBalance !== null">{{ formatCurrency(creditBalanceDisplay, creditBalanceCurrency) }}</span>
            <span class="card-value text-muted" v-else>—</span>
          </div>
        </div>
      </div>

      <!-- Subscriptions Table -->
      <div class="usage-table-card" v-if="filteredSubscriptions && filteredSubscriptions.length > 0">
        <h5 class="table-title">{{ $t('billingUsage.subscriptionsBreakdown') }}</h5>
        <b-table
          :items="filteredSubscriptions"
          :fields="subscriptionFields"
          responsive
          striped
          hover
          show-empty
          class="usage-table"
        >
          <template #cell(product)="data">
            <div class="service-cell">
              <div class="service-icon">
                <b-icon icon="box" font-scale="1.2"></b-icon>
              </div>
              <div class="service-info">
                <span class="service-name">{{ data.item.license_product.name }}</span>
                <span class="service-code">{{ data.item.license_product.group }}</span>
              </div>
            </div>
          </template>

          <template #cell(status)="data">
            <b-badge :variant="getSubscriptionStatusVariant(data.item)">
              {{ getSubscriptionStatusText(data.item) }}
            </b-badge>
          </template>

          <template #cell(period)="data">
            <span>{{ data.item.period === 12 ? $t('subscription.year') : $t('subscription.month') }}</span>
          </template>

          <template #cell(price)="data">
            <div class="price-cell">
              <span class="amount-value">{{ formatCurrency(data.item.human_price, data.item.currency) }}</span>
              <span class="price-period">/ {{ data.item.period === 12 ? $t('subscription.year') : $t('subscription.month') }}</span>
            </div>
          </template>

          <template #cell(monthly_cost)="data">
            <div class="monthly-cost-cell">
              <span class="amount-value">{{ formatCurrency(getMonthlyPrice(data.item), data.item.currency) }}</span>
              <span class="monthly-label">/ {{ $t('subscription.month') }}</span>
              <b-badge v-if="data.item.period === 12" variant="info" class="ml-2">{{ $t('billingUsage.annualPlan') }}</b-badge>
            </div>
          </template>

          <template #cell(expires_at)="data">
            <span class="recorded-date">{{ formatDate(data.item.expires_at) }}</span>
          </template>

          <template #empty>
            <div class="text-center text-muted py-4">
              {{ $t('subscription.noSubscriptions') }}
            </div>
          </template>
        </b-table>
      </div>

      <!-- Usage Table -->
      <div class="usage-table-card">
        <h5 class="table-title">{{ $t('billingUsage.usageBreakdown') }}</h5>
        <b-table
          :items="usageData.usage"
          :fields="fields"
          :busy="loading"
          responsive
          striped
          hover
          show-empty
          class="usage-table"
        >
          <template #cell(option_code)="data">
            <div class="service-cell">
              <div class="service-icon">
                <b-icon :icon="getServiceIcon(data.item.option_code)" font-scale="1.2"></b-icon>
              </div>
              <div class="service-info">
                <span class="service-name">{{ data.item.product_id || '-' }}</span>
                <span class="service-code">{{ data.item.option_code }}</span>
              </div>
            </div>
          </template>

          <template #cell(count)="data">
            <span class="usage-count">{{ formatCount(data.item.units, data.item.unit_price && data.item.unit_price.unit) }}</span>
          </template>

          <template #cell(unit_price)="data">
            <div class="unit-price-cell" v-if="data.item.unit_price">
              <span class="unit-price">
                {{ formatUnitPrice(data.item.unit_price, data.item.currency, data.item.charging_mode, data.item.block_size) }}
              </span>
              <b-badge v-if="data.item.charging_mode === 'per_block'" variant="info" class="ml-1 block-badge" v-b-tooltip.hover :title="$t('billingUsage.perBlockTooltip', { size: data.item.block_size ? data.item.block_size.toLocaleString() : '0', unit: data.item.unit_price.unit || 'units' })">
                {{ $t('billingUsage.perBlock') }}
              </b-badge>
            </div>
            <span class="unit-price" v-else>-</span>
          </template>

          <template #cell(amount)="data">
            <div class="amount-cell">
              <span class="amount-value">
                {{ formatCurrency(data.item.total_amount / 100, data.item.currency) }}
              </span>
              <span v-if="data.item.charging_mode === 'per_block'" class="block-note">
                {{ formatBlockCount(data.item.units, data.item.block_size) }} {{ $t('billingUsage.blocks') }}
              </span>
            </div>
          </template>

          <template #cell(recorded_at)="data">
            <span class="recorded-date">{{ formatDate(data.item.CreatedAt) }}</span>
          </template>

          <template #cell(usage_start)="data">
            <span class="recorded-date">{{ formatDateOnly(data.item.usage_start) }}</span>
          </template>

          <template #cell(usage_end)="data">
            <span class="recorded-date">{{ formatDateOnly(data.item.usage_end) }}</span>
          </template>

          <template #empty>
            <div class="text-center text-muted py-4">
              {{ $t('billingUsage.noUsage') }}
            </div>
          </template>
        </b-table>
      </div>

      <!-- Credit Activity -->
      <div class="usage-table-card" v-if="creditBalance !== null">
        <div class="table-header-row">
          <h5 class="table-title">{{ $t('billingUsage.creditActivity.title') }}</h5>
        </div>

        <!-- Daily Deduction Chart -->
        <div class="credit-chart-section mb-4" v-if="dailyChartDays > 0">
          <div class="chart-container" style="position:relative; height:280px; width:100%">
            <canvas ref="dailyChartCanvas"></canvas>
          </div>
        </div>

        <div v-if="creditHistoryLoading" class="text-center py-4">
          <b-spinner variant="primary" small class="mr-2"></b-spinner>
          {{ $t('billingUsage.creditActivity.loading') }}
        </div>

        <b-table
          v-else
          :items="creditHistory"
          :fields="creditHistoryFields"
          responsive
          striped
          hover
          show-empty
          class="usage-table"
        >
          <template #cell(type)="data">
            <b-badge variant="success">
              {{ $t('billingUsage.creditActivity.topup') }}
            </b-badge>
          </template>

          <template #cell(service)="data">
            <div v-if="data.item.product_id || data.item.option_code" class="service-cell">
              <div class="service-info">
                <span class="service-name">{{ data.item.product_id || '—' }}</span>
                <span class="service-code">{{ data.item.option_code || '' }}</span>
              </div>
            </div>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell(amount)="data">
            <span class="credit-positive">
              +{{ formatCurrency(data.item.amount_display, data.item.currency) }}
            </span>
          </template>

          <template #cell(event_count)="data">
            <b-badge v-if="data.item.type === 'deduction' && data.item.event_count > 1" variant="secondary">
              {{ data.item.event_count }}
            </b-badge>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell(created_at)="data">
            <span class="recorded-date">{{ formatDateTime(data.item.created_at) }}</span>
          </template>

          <template #empty>
            <div class="text-center text-muted py-4">
              {{ $t('billingUsage.creditActivity.empty') }}
            </div>
          </template>
        </b-table>

        <!-- Pagination -->
        <div class="credit-pagination" v-if="creditHistoryTotal > creditHistoryLimit">
          <b-pagination
            v-model="creditHistoryPage"
            :total-rows="creditHistoryTotal"
            :per-page="creditHistoryLimit"
            size="sm"
            @change="loadCreditHistory"
          ></b-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/_custom.scss';

.billing-usage-container {
  padding: 24px;
}

.usage-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;

  .usage-title {
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 8px;
  }

  .usage-subtitle {
    color: var(--text-secondary);
    font-size: 1rem;
    margin: 0;
  }

  .header-actions {
    display: flex;
    gap: 12px;
  }
}

.month-selector-card {
  background: $dark-background;
  border: 1px solid $dark-border2;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 24px;

  .selector-group {
    display: flex;
    gap: 12px;
    max-width: 400px;

    .month-select,
    .year-select {
      background: $dark-hard2-background;
      border: 1px solid $dark-border2;
      color: var(--text-primary);
      border-radius: 8px;
      padding: 10px 16px;
      height: 42px;

      &:focus {
        background: $dark-hard2-background;
        border-color: $protokol-royal-active;
        color: var(--text-primary);
      }

      option {
        background: $dark-hard2-background;
        color: var(--text-primary);
        padding: 8px;
      }
    }

    .month-select {
      flex: 2;
    }

    .year-select {
      flex: 1;
    }
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--text-secondary);
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: $dark-background;
  border: 1px solid $dark-border2;
  border-radius: 16px;

  .empty-icon {
    color: var(--text-muted);
    margin-bottom: 24px;
  }

  h4 {
    color: var(--text-primary);
    font-weight: 600;
    margin-bottom: 8px;
  }

  p {
    color: var(--text-secondary);
    margin: 0;
  }
}

.usage-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.summary-card {
  background: $dark-background;
  border: 1px solid $dark-border2;
  border-radius: 12px;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  .card-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .card-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .card-label {
    font-size: 0.85rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .card-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-primary);
  }
}

.total-card {
  .card-icon {
    background: rgba(34, 197, 94, 0.15);
    color: #22c55e;
  }

  .card-value {
    color: #22c55e;
  }
}

.services-card {
  .card-icon {
    background: rgba(59, 130, 246, 0.15);
    color: #3b82f6;
  }

  .card-value {
    color: #3b82f6;
  }
}

.subscriptions-card {
  .card-icon {
    background: rgba(168, 85, 247, 0.15);
    color: #a855f7;
  }

  .card-value {
    color: #a855f7;
  }
}

.total-charges-card {
  .card-icon {
    background: rgba(245, 158, 11, 0.15);
    color: #f59e0b;
  }

  .card-value {
    color: #f59e0b;
  }
}

.credits-card {
  .card-icon {
    background: rgba(20, 184, 166, 0.15);
    color: #14b8a6;
  }

  .card-value {
    color: #14b8a6;
  }
}

.usage-table-card {
  background: $dark-background;
  border: 1px solid $dark-border2;
  border-radius: 12px;
  overflow: hidden;

  .table-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text-primary);
    padding: 20px 24px;
    margin: 0;
    border-bottom: 1px solid $dark-border2;
  }
}

::v-deep .usage-table {
  margin: 0;

  thead th {
    background: $dark-hard2-background;
    border-color: $dark-border2;
    color: var(--text-secondary);
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 16px 24px;
  }

  tbody td {
    background: transparent;
    border-color: $dark-border2;
    color: var(--text-primary);
    padding: 16px 24px;
    vertical-align: middle;
  }

  tbody tr {
    &:hover {
      background: rgba(255, 255, 255, 0.02);
    }
  }
}

.service-cell {
  display: flex;
  align-items: center;
  gap: 12px;

  .service-icon {
    width: 40px;
    height: 40px;
    background: rgba(102, 126, 234, 0.15);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $protokol-royal-active;
  }

  .service-info {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .service-name {
      font-weight: 600;
      color: var(--text-primary);
    }

    .service-code {
      font-size: 0.8rem;
      color: var(--text-muted);
    }
  }
}

.usage-count {
  font-weight: 600;
  color: var(--text-primary);
}

.unit-price {
  color: var(--text-primary);
}

.unit-price-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.block-badge {
  font-size: 0.7rem;
  font-weight: 500;
  cursor: help;
  padding: 2px 6px;
}

.amount-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.block-note {
  font-size: 0.75rem;
  color: var(--text-muted);
}

.amount-value {
  font-weight: 700;
  color: $protokol-royal-active;
  font-size: 1.05rem;
}

.recorded-date {
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.price-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;

  .price-period {
    font-size: 0.75rem;
    color: var(--text-muted);
  }
}

.monthly-cost-cell {
  display: flex;
  align-items: center;
  gap: 4px;

  .monthly-label {
    font-size: 0.75rem;
    color: var(--text-muted);
  }
}

@media (max-width: 768px) {
  .usage-header {
    flex-direction: column;
    gap: 16px;
  }

  .month-selector-card .selector-group {
    max-width: 100%;
  }

  .summary-cards {
    grid-template-columns: 1fr;
  }
}

.table-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid $dark-border2;

  .table-title {
    padding: 0;
    border-bottom: none;
    margin: 0;
  }

  .credit-filter-group {
    display: flex;
    align-items: center;
  }
}

.credit-positive {
  font-weight: 700;
  color: #22c55e;
  font-size: 1.05rem;
}

.credit-negative {
  font-weight: 700;
  color: #f59e0b;
  font-size: 1.05rem;
}

.credit-pagination {
  display: flex;
  justify-content: center;
  padding: 16px 24px;
  border-top: 1px solid $dark-border2;
}
</style>

<script>
import Chart from 'chart.js'

export default {
  name: 'BillingUsage',
  data() {
    return {
      loading: false,
      usageData: null,
      subscriptions: null,
      creditBalance: null,
      creditBalanceDisplay: null,
      creditBalanceCurrency: 'USD',
      creditHistory: [],
      creditHistoryTotal: 0,
      creditHistoryPage: 1,
      creditHistoryLimit: 50,
      creditHistoryLoading: false,
      dailyChartData: null,
      dailyChartDays: 0,
      dailyChartCurrency: 'USD',
      selectedYear: new Date().getFullYear(),
      selectedMonth: new Date().getMonth() + 1,
    }
  },

  computed: {
    fields() {
      return [
        {
          key: 'option_code',
          label: this.$t('billingUsage.service'),
          sortable: true,
        },
        {
          key: 'count',
          label: this.$t('billingUsage.usage'),
          sortable: true,
        },
        {
          key: 'unit_price',
          label: this.$t('billingUsage.unitPrice'),
          sortable: true,
        },
        {
          key: 'amount',
          label: this.$t('billingUsage.amount'),
          sortable: true,
        },
        {
          key: 'recorded_at',
          label: this.$t('billingUsage.recordedAt'),
          sortable: true,
        },
        {
          key: 'usage_start',
          label: this.$t('billingUsage.usageStart'),
          sortable: true,
        },
        {
          key: 'usage_end',
          label: this.$t('billingUsage.usageEnd'),
          sortable: true,
        },
      ]
    },

    subscriptionFields() {
      return [
        {
          key: 'product',
          label: this.$t('billingUsage.product'),
          sortable: true,
        },
        {
          key: 'status',
          label: this.$t('invoices.status'),
          sortable: true,
        },
        {
          key: 'period',
          label: this.$t('billingUsage.period'),
          sortable: true,
        },
        {
          key: 'price',
          label: this.$t('billingUsage.totalPrice'),
          sortable: true,
        },
        {
          key: 'monthly_cost',
          label: this.$t('billingUsage.monthlyCost'),
          sortable: true,
        },
        {
          key: 'expires_at',
          label: this.$t('subscription.expiresAt'),
          sortable: true,
        },
      ]
    },

    monthOptions() {
      const months = [
        this.$t('january'),
        this.$t('february'),
        this.$t('march'),
        this.$t('april'),
        this.$t('may'),
        this.$t('june'),
        this.$t('july'),
        this.$t('august'),
        this.$t('september'),
        this.$t('october'),
        this.$t('november'),
        this.$t('december'),
      ]
      return months.map((name, index) => ({
        value: index + 1,
        text: name,
      }))
    },

    yearOptions() {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let year = 2020; year <= currentYear; year++) {
        years.push({ value: year, text: year.toString() })
      }
      return years.reverse()
    },

    totalAmount() {
      if (!this.usageData || !this.usageData.total_amount) return 0
      // Root total_amount is always in cents (precision 100)
      return this.usageData.total_amount / 100
    },

    currency() {
      if (!this.usageData) return 'EUR'
      return this.usageData.currency || 'EUR'
    },

    subscriptionCount() {
      if (!this.filteredSubscriptions) return 0
      return this.filteredSubscriptions.length
    },

    subscriptionTotal() {
      if (!this.filteredSubscriptions) return 0
      // Calculate total monthly cost (annual subscriptions divided by 12)
      return this.filteredSubscriptions
        .reduce((sum, sub) => {
          const monthlyPrice = this.getMonthlyPrice(sub)
          return sum + monthlyPrice
        }, 0)
    },

    creditHistoryFields() {
      return [
        { key: 'type', label: this.$t('billingUsage.creditActivity.type'), sortable: false },
        { key: 'service', label: this.$t('billingUsage.creditActivity.service'), sortable: false },
        { key: 'amount', label: this.$t('billingUsage.creditActivity.amount'), sortable: false },
        { key: 'event_count', label: this.$t('billingUsage.creditActivity.events'), sortable: false },
        { key: 'created_at', label: this.$t('billingUsage.creditActivity.date'), sortable: false },
      ]
    },

    filteredSubscriptions() {
      if (!this.subscriptions) return []
      
      // First, group subscriptions by ref_no and inherit properties from master subscription
      const groupedSubscriptions = {}
      
      this.subscriptions.forEach(sub => {
        const refNo = sub.ref_no
        
        if (!groupedSubscriptions[refNo]) {
          groupedSubscriptions[refNo] = []
        }
        
        groupedSubscriptions[refNo].push(sub)
      })
      
      // Process each group and create enhanced subscriptions
      const enhancedSubscriptions = []
      
      Object.keys(groupedSubscriptions).forEach(refNo => {
        const group = groupedSubscriptions[refNo]
        
        // Find the master subscription (the one with license_code)
        const master = group.find(sub => sub.license_code) || group[0]
        
        // Add all subscriptions in the group with inherited properties from master
        group.forEach(sub => {
          enhancedSubscriptions.push({
            ...sub,
            // Inherit these properties from master subscription
            is_expired: master.is_expired,
            is_canceled: master.is_canceled,
            expires_at: master.expires_at,
            CanceledAt: master.CanceledAt,
            // Keep track of whether this is the master
            isMaster: sub.license_code === master.license_code,
            masterLicenseCode: master.license_code
          })
        })
      })
      
      // Create date range for the selected month
      const startOfMonth = new Date(this.selectedYear, this.selectedMonth - 1, 1)
      const endOfMonth = new Date(this.selectedYear, this.selectedMonth, 0, 23, 59, 59, 999)
      
      return enhancedSubscriptions.filter(sub => {
        // Don't show expired subscriptions
        if (sub.is_expired) return false
        
        const createdAt = new Date(sub.CreatedAt)
        const expiresAt = sub.expires_at ? new Date(sub.expires_at) : null
        
        // Subscription must have been created before or during the month
        if (createdAt > endOfMonth) return false
        
        // If subscription has expiry date, it must not have expired before the month started
        if (expiresAt && expiresAt < startOfMonth) return false
        
        // If subscription is canceled, check if it was canceled after the month started
        if (sub.is_canceled && sub.CanceledAt) {
          const canceledAt = new Date(sub.CanceledAt)
          if (canceledAt < startOfMonth) return false
        }
        
        return true
      })
    },
  },

  methods: {
    async loadUsage() {
      this.loading = true
      try {
        const [usageResponse, subscriptionsResponse] = await Promise.all([
          this.$transactionApi.get('/billing/usage', {
            params: {
              year: this.selectedYear,
              month: this.selectedMonth,
            },
          }),
          this.$store.dispatch('fetchSubscriptions')
        ])
        
        this.usageData = usageResponse.data
        this.subscriptions = this.$store.getters.getSubscriptions
      } catch (error) {
        console.error('Failed to load usage:', error)
        this.$bvToast.toast(
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : this.$t('billingUsage.loadError'),
          {
            variant: 'danger',
            solid: true,
          }
        )
      } finally {
        this.loading = false
      }

      // Fetch credit balance independently so a failure doesn't block the page
      try {
        const balanceResponse = await this.$transactionApi.get('/billing/balance')
        this.creditBalance = balanceResponse.data && balanceResponse.data.balance_usd !== undefined
          ? balanceResponse.data.balance_usd
          : null
        if (balanceResponse.data && balanceResponse.data.balance_display != null) {
          this.creditBalanceDisplay = balanceResponse.data.balance_display
        } else {
          this.creditBalanceDisplay = this.creditBalance
        }
        if (balanceResponse.data && balanceResponse.data.currency) {
          this.creditBalanceCurrency = balanceResponse.data.currency
        }
        if (this.creditBalance !== null) {
          this.loadCreditHistory(1)
          this.loadDailyChart()
        }
      } catch (e) {
        this.creditBalance = null
        this.creditBalanceDisplay = null
        this.creditBalanceCurrency = 'USD'
      }
    },

    async loadCreditHistory(page) {
      if (page) {
        this.creditHistoryPage = page
      }
      this.creditHistoryLoading = true
      try {
        var offset = (this.creditHistoryPage - 1) * this.creditHistoryLimit
        var params = { limit: this.creditHistoryLimit, offset: offset, type: 'topup', year: this.selectedYear, month: this.selectedMonth }
        var resp = await this.$transactionApi.get('/billing/credits/history', { params: params })
        this.creditHistory = resp.data.items || []
        this.creditHistoryTotal = resp.data.total || 0
      } catch (e) {
        this.creditHistory = []
        this.creditHistoryTotal = 0
      } finally {
        this.creditHistoryLoading = false
      }
    },

    async loadDailyChart() {
      try {
        var resp = await this.$transactionApi.get('/billing/credits/daily', {
          params: { year: this.selectedYear, month: this.selectedMonth }
        })
        var items = resp.data.items || []
        this.dailyChartCurrency = resp.data.currency || 'USD'

        if (items.length === 0) {
          this.dailyChartDays = 0
          return
        }

        // Pivot: group by date, with each option_code as a dataset
        var dates = []
        var dateMap = {}
        var optionCodes = []
        var optionSet = {}
        items.forEach(function(item) {
          if (!dateMap[item.date]) {
            dateMap[item.date] = {}
            dates.push(item.date)
          }
          dateMap[item.date][item.option_code] = item.amount_display
          if (!optionSet[item.option_code]) {
            optionSet[item.option_code] = true
            optionCodes.push(item.option_code)
          }
        })

        this.dailyChartDays = dates.length

        // Build datasets (one per option_code) — all same color, stacked
        var barColor = '#667eea'
        var datasets = optionCodes.map(function(code) {
          return {
            label: code,
            data: dates.map(function(d) { return dateMap[d][code] || 0 }),
            backgroundColor: barColor,
            borderColor: barColor,
            borderWidth: 0
          }
        })

        var self = this
        this.$nextTick(function() {
          var canvas = self.$refs.dailyChartCanvas
          if (!canvas) return

          // Destroy previous chart instance if any
          if (self._dailyChart) {
            self._dailyChart.destroy()
          }

          var ctx = canvas.getContext('2d')
          self._dailyChart = new Chart(ctx, {
            type: 'bar',
            data: {
              labels: dates,
              datasets: datasets
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                xAxes: [{
                  stacked: true,
                  gridLines: { display: false },
                  ticks: { maxRotation: 45, fontColor: '#aaa' }
                }],
                yAxes: [{
                  stacked: true,
                  gridLines: { color: 'rgba(255,255,255,0.05)' },
                  ticks: {
                    fontColor: '#aaa',
                    callback: function(value) {
                      return self.formatChartCurrency(value)
                    }
                  }
                }]
              },
              legend: {
                position: 'bottom',
                labels: { fontColor: '#aaa', boxWidth: 12, padding: 16 }
              },
              tooltips: {
                mode: 'index',
                intersect: false,
                callbacks: {
                  label: function(tooltipItem, data) {
                    var ds = data.datasets[tooltipItem.datasetIndex]
                    var val = tooltipItem.yLabel
                    return ds.label + ': ' + self.formatChartCurrency(val)
                  }
                }
              },
              hover: {
                mode: 'index',
                intersect: false
              }
            }
          })
        })
      } catch (e) {
        this.dailyChartDays = 0
      }
    },

    formatChartCurrency(value) {
      if (this.dailyChartCurrency === 'USD') {
        return '$' + value.toFixed(2)
      }
      return value.toFixed(2) + ' ' + this.dailyChartCurrency
    },

    formatOptionCode(code) {
      const labels = {
        'api_calls': this.$t('billingUsage.services.apiCalls'),
        'storage_gb': this.$t('billingUsage.services.storage'),
        'bandwidth_gb': this.$t('billingUsage.services.bandwidth'),
        'compute_hours': this.$t('billingUsage.services.computeTime'),
        'database_queries': this.$t('billingUsage.services.databaseQueries'),
        'email_sends': this.$t('billingUsage.services.emails'),
        'sms_sends': this.$t('billingUsage.services.sms'),
        'component-execution-time': 'Execution Time',
        'component-docs-examined': 'Docs Examined',
        'component-bytes-returned': 'Bytes Returned',
        'component-docs-returned': 'Docs Returned',
        'forge-apps': 'Forge Apps',
        'cortex-ocr-seconds': 'Kortex OCR',
        'cortex-ocr-pages': 'Kortex OCR Pages',
        'cortex-prompt-tokens': 'Kortex Prompt Tokens',
        'cortex-completion-tokens': 'Kortex Completion Tokens',
        'cortex-embedding-tokens': 'Kortex Embeddings',
      }
      return labels[code] || code
    },

    formatBlockCount(units, blockSize) {
      if (!blockSize || blockSize <= 0) return '0'
      return Math.ceil(units / blockSize).toLocaleString()
    },

    formatCount(count, unit) {
      if (count === undefined || count === null) return '-'
      
      // Convert milliseconds to minutes for component execution time
      if (unit === 'ms' || unit === 'milliseconds') {
        const minutes = count / (1000 * 60)
        return `${minutes.toFixed(2)} min`
      }
      
      // Convert bytes to appropriate unit
      if (unit === 'byte' || unit === 'bytes') {
        if (count >= 1024 * 1024 * 1024) {
          return `${(count / (1024 * 1024 * 1024)).toFixed(2)} GB`
        } else if (count >= 1024 * 1024) {
          return `${(count / (1024 * 1024)).toFixed(2)} MB`
        } else if (count >= 1024) {
          return `${(count / 1024).toFixed(2)} KB`
        }
        return `${count.toLocaleString()} ${unit}`
      }
      
      // Convert protokol sandbox mb to GB
      if (unit === 'protokol sandbox mb' || unit === 'mb' || unit === 'MB') {
        return `${(count / 1024).toFixed(2)} GB`
      }
      
      // If unit is provided from API, use it directly
      if (unit) {
        return `${count.toLocaleString()} ${unit}`
      }
      // Fallback for backward compatibility
      return `${count.toLocaleString()} ${this.$t('billingUsage.units.units')}`
    },

    formatCurrency(amount, currency = 'EUR') {
      if (amount === undefined || amount === null) return '-'
      const prefixSymbols = { USD: '$', EUR: '€' }
      const symbol = prefixSymbols[currency]
      
      // For very small amounts, use more decimal places and remove trailing zeros
      if (amount < 0.01 && amount > 0) {
        const formatted = amount.toFixed(8).replace(/\.?0+$/, '')
        return symbol ? `${symbol}${formatted}` : `${formatted} ${currency}`
      }
      
      return symbol ? `${symbol}${amount.toFixed(2)}` : `${amount.toFixed(2)} ${currency}`
    },

    formatUnitPrice(unitPriceObj, currency = 'EUR', chargingMode = '', blockSize = 0) {
      if (!unitPriceObj || !unitPriceObj.value || !unitPriceObj.precision) return '-'
      
      const prefixSymbols = { USD: '$', EUR: '€' }
      const symbol = prefixSymbols[currency]
      
      const fmtAmount = (n) => symbol ? `${symbol}${n}` : `${n} ${currency}`
      
      let pricePerUnit = unitPriceObj.value / unitPriceObj.precision
      let unit = unitPriceObj.unit || 'unit'
      let multiplier = 1
      
      // Per-block pricing: show price per block (e.g., "$0.50 / 1,000 emails")
      if (chargingMode === 'per_block' && blockSize > 0) {
        // pricePerUnit is already the block price in dollars (value/precision = cents/100)
        const pluralUnit = blockSize > 1 ? `${unit}s` : unit
        return `${fmtAmount(pricePerUnit.toFixed(2))} / ${blockSize.toLocaleString()} ${pluralUnit}`
      }
      
      // Convert bytes to GB (1 GB = 1,000,000,000 bytes)
      if (unit === 'byte' || unit === 'bytes') {
        const bytesInGB = 1000000000
        pricePerUnit = pricePerUnit * bytesInGB
        unit = 'GB'
      }
      // Convert protokol sandbox mb to GB (1 GB = 1,000 MB)
      else if (unit === 'protokol sandbox mb' || unit === 'mb' || unit === 'MB') {
        const mbInGB = 1000
        pricePerUnit = pricePerUnit * mbInGB
        unit = 'GB'
      }
      // Convert milliseconds to minutes
      else if (unit === 'ms' || unit === 'millisecond' || unit === 'milliseconds') {
        const msInMinute = 60 * 1000
        pricePerUnit = pricePerUnit * msInMinute
        unit = 'minute'
      }
      
      // If price is still very small, find a better multiplier to show
      if (pricePerUnit < 0.01 && pricePerUnit > 0) {
        // Calculate how many units we need to get to at least $1
        const baseMultiplier = Math.ceil(1 / pricePerUnit)
        
        // Round to nice numbers
        if (baseMultiplier < 100) {
          multiplier = Math.ceil(baseMultiplier / 10) * 10
        } else if (baseMultiplier < 1000) {
          multiplier = Math.ceil(baseMultiplier / 100) * 100
        } else if (baseMultiplier < 10000) {
          multiplier = Math.ceil(baseMultiplier / 1000) * 1000
        } else if (baseMultiplier < 100000) {
          multiplier = Math.ceil(baseMultiplier / 10000) * 10000
        } else if (baseMultiplier < 1000000) {
          multiplier = Math.ceil(baseMultiplier / 100000) * 100000
        } else {
          multiplier = Math.ceil(baseMultiplier / 1000000) * 1000000
        }
        
        const adjustedPrice = pricePerUnit * multiplier
        return `${fmtAmount(adjustedPrice.toFixed(2))} / ${multiplier.toLocaleString()} ${unit}${multiplier > 1 && unit === 'minute' ? 's' : ''}`
      }
      
      // For normal prices, show per 1 unit
      const displayUnit = unit === 'minute' ? 'minute' : unit
      return `${fmtAmount(pricePerUnit.toFixed(2))} / 1 ${displayUnit}`
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    formatDateOnly(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    getServiceIcon(code) {
      const icons = {
        'api_calls': 'cloud-arrow-up',
        'storage_gb': 'hdd',
        'bandwidth_gb': 'arrows-expand',
        'compute_hours': 'cpu',
        'database_queries': 'database',
        'email_sends': 'envelope',
        'sms_sends': 'chat-dots',
        'component-execution-time': 'clock',
        'component-docs-examined': 'file-earmark-text',
        'component-bytes-returned': 'file-binary',
        'component-docs-returned': 'file-earmark-check',
        'forge-apps': 'app-indicator',
        'cortex-ocr-seconds': 'file-earmark-richtext',
        'cortex-ocr-pages': 'file-earmark-richtext',
        'cortex-prompt-tokens': 'chat-text',
        'cortex-completion-tokens': 'chat-text',
        'cortex-embedding-tokens': 'diagram-3',
      }
      return icons[code] || 'gear'
    },

    getSubscriptionStatusVariant(subscription) {
      if (subscription.is_canceled) return 'danger'
      if (subscription.is_expired) return 'warning'
      if (subscription.is_paused) return 'secondary'
      if (subscription.is_active) return 'success'
      return 'secondary'
    },

    getSubscriptionStatusText(subscription) {
      if (subscription.is_canceled) return this.$t('subscription.canceled')
      if (subscription.is_expired) return this.$t('expired')
      if (subscription.is_paused) return this.$t('subscription.paused')
      if (subscription.is_active) return this.$t('active')
      return this.$t('inactive')
    },

    calculateHumanPrice(value, precision) {
      if (!value || !precision) return 0
      return value / precision
    },

    getMonthlyPrice(subscription) {
      if (!subscription || !subscription.human_price) return 0
      // If subscription is expired, monthly cost is 0
      if (subscription.is_expired) return 0
      // If annual subscription, divide by 12 to get monthly cost
      if (subscription.period === 12) {
        return subscription.human_price / 12
      }
      // Otherwise return the price as-is (already monthly)
      return subscription.human_price
    },
  },

  mounted() {
    this.loadUsage()
  },
}
</script>
